import { Paper, Skeleton, TableContainer } from '@mui/material';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import { Dropdown } from '@commons/utils/styledLibraryComponents';
import { getTheme } from '@commons/utils/theme';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import { generateUUID } from '@commons/utils/uuid';

import { getIntegrationExternalEntities, getIntegrationMappings } from '@services/integrations';
import { getStoresOfClient } from '@services/store';

import { PENNYLANE_OAUTH2_CODE } from '../../../../utils/constants';

import {
  AddRowButton,
  CellContent,
  CustomerItemContent,
  CustomerItemId,
  DropdownContainer,
  ExternalEntityMappingPanelRoot,
  IntroText,
  MappingTablesHost,
  RemoveRowButton,
  RowActions,
} from './styledComponents';

/** z-index above drawer content; list stays `position: absolute` (from Dropdown) so it scrolls with the drawer body. */
const DROPDOWN_LIST_LAYER_STYLE = {
  zIndex: 1300,
};

const getMappingCellSx = (theme) => ({
  borderLeft: `1px solid ${theme.colors.greys.light}`,
  borderBottom: `1px solid ${theme.colors.greys.light}`,
  minWidth: 0,
  overflow: 'visible',
  padding: 0,
  position: 'relative',
});

// Grey link icon for the Inpulse store column (disabled, already used in another row);
// black link icon for the Pennylane customer column (kept selectable for n:1 mappings).
const STORE_LINKED_ICON_SRC = '/images/inpulse/link-grey.svg';
const CUSTOMER_LINKED_ICON_SRC = '/images/inpulse/link-ip-black.svg';

const buildAlreadyLinkedItem = (id, label) => ({
  id,
  value: label,
  isDisabled: true,
  renderValue: () => (
    <>
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </span>
      <img alt="" height={16} src={STORE_LINKED_ICON_SRC} style={{ flexShrink: 0 }} width={16} />
    </>
  ),
});

// A Pennylane customer already used in another row is kept selectable (n:1: several Inpulse
// stores can point to the same customer). `isLinked` only drives the visual hint (link icon),
// while `isDisabled` is decoupled so we keep the icon without blocking re-selection.
const buildCustomerItem = (id, name, { isLinked = false, isDisabled = false } = {}) => ({
  id,
  value: name,
  isDisabled,
  renderValue: () => (
    <>
      <CustomerItemContent>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name}
        </span>
        <CustomerItemId>{id}</CustomerItemId>
      </CustomerItemContent>
      {isLinked && (
        <img
          alt=""
          height={16}
          src={CUSTOMER_LINKED_ICON_SRC}
          style={{ flexShrink: 0 }}
          width={16}
        />
      )}
    </>
  ),
});

const createMappingRow = () => ({
  localEntityId: null,
  partnerEntityId: null,
  rowId: generateUUID(),
});

const getStoreLabel = (store) => store?.name ?? '';

const areMappingRowsValid = (rows, storesList, partnerEntities) => {
  if (!rows.length) {
    return true;
  }

  return rows.every((row) => {
    const hasLocal = Boolean(row.localEntityId);
    const hasPartner = Boolean(row.partnerEntityId);

    if (!hasLocal || !hasPartner) {
      return false;
    }

    const localUnknown =
      storesList.length > 0 && !storesList.find(({ id }) => id === row.localEntityId);

    const partnerUnknown =
      partnerEntities.length > 0 &&
      !partnerEntities.find((entity) => String(entity.id) === row.partnerEntityId);

    return !localUnknown && !partnerUnknown;
  });
};

const buildIntegrationMappingsPayload = (rows) =>
  rows.reduce((acc, { localEntityId, partnerEntityId }) => {
    if (!localEntityId || !partnerEntityId) {
      return acc;
    }

    const externalId = String(partnerEntityId).trim();

    if (!externalId) {
      return acc;
    }

    acc.push({ storeId: localEntityId, externalId });
    return acc;
  }, []);

export const ExternalEntityMappingPanel = ({
  externalEntitiesRefreshKey = 0,
  initialMappings,
  initialStores,
  isKitchenContext = false,
  linkedStoreIds = null,
  onExternalEntitiesError = null,
  onInitialMappingsLoaded = null,
  onMappingChange,
  partnerConfigId = null,
  partnerId,
  partnerName = '',
  requiredMappings: requiredMappingsProp,
  storeId = null,
  integrationCode,
}) => {
  const { t } = useTranslation();
  const theme = getTheme();
  const tableRef = useRef(null);
  const [columnWidth, setColumnWidth] = useState(null);
  const [storesList, setStoresList] = useState(initialStores ?? []);
  const [partnerEntities, setPartnerEntities] = useState([]);
  const [savedMappings, setSavedMappings] = useState(initialMappings ?? null);
  const [rows, setRows] = useState([]);
  const hasReportedInitialRef = useRef(false);

  const hasRequiredMappings = (requiredMappingsProp?.length ?? 0) > 0;

  const [isLoadingStores, setIsLoadingStores] = useState(false);
  const [isLoadingPartnerEntities, setIsLoadingPartnerEntities] = useState(() =>
    Boolean(partnerConfigId && hasRequiredMappings),
  );

  const isAnyEntityDataLoading = isLoadingStores || isLoadingPartnerEntities;

  const displayedStoresList = useMemo(() => {
    const activeStores = storesList.filter(({ active }) => active);

    if (!linkedStoreIds) {
      return activeStores;
    }

    return activeStores.filter(({ id }) => linkedStoreIds.includes(id));
  }, [storesList, linkedStoreIds]);

  useEffect(() => {
    if (!hasRequiredMappings) {
      setStoresList([]);
      setIsLoadingStores(false);
      return;
    }

    let cancelled = false;

    const fetchStores = async () => {
      setIsLoadingStores(true);

      try {
        const data = await getStoresOfClient();

        if (cancelled) {
          return;
        }

        const allStores = Array.isArray(data) ? data : [];
        let scopedStores = allStores;

        if (integrationCode === PENNYLANE_OAUTH2_CODE) {
          scopedStores = allStores.filter((store) => !store.isKitchen);
        } else if (storeId) {
          scopedStores = allStores.filter((store) => store.id === storeId);
        }

        setStoresList(scopedStores);
      } catch {
        if (cancelled) {
          return;
        }

        setStoresList([]);
      } finally {
        if (!cancelled) {
          setIsLoadingStores(false);
        }
      }
    };

    fetchStores();

    return () => {
      cancelled = true;
    };
  }, [hasRequiredMappings, storeId, integrationCode]);

  useEffect(() => {
    if (!hasRequiredMappings || !partnerConfigId) {
      setPartnerEntities([]);
      setIsLoadingPartnerEntities(false);
      return;
    }

    let cancelled = false;

    const fetchPartnerEntities = async () => {
      setIsLoadingPartnerEntities(true);

      try {
        const data = await getIntegrationExternalEntities(partnerConfigId);

        if (cancelled) {
          return;
        }

        setPartnerEntities(Array.isArray(data) ? data : []);
        onExternalEntitiesError?.(null);
      } catch (err) {
        if (cancelled) {
          return;
        }

        setPartnerEntities([]);

        // A 422 on this endpoint means the Pennylane OAuth token is dead ; the
        // connection must be re-established, so surface the actionable reconnect message.
        const isReauthRequired =
          err?.response?.status === 422 && integrationCode === PENNYLANE_OAUTH2_CODE;

        let message;
        if (isReauthRequired) {
          message = t('ADMIN.INTEGRATIONS.PENNYLANE_OAUTH2_REAUTH_REQUIRED_ERROR');
        } else {
          message =
            err?.response?.data?.message ??
            err?.message ??
            t('ADMIN.INTEGRATIONS.EXTERNAL_ENTITIES_FETCH_ERROR');
        }

        onExternalEntitiesError?.(message);
      } finally {
        if (!cancelled) {
          setIsLoadingPartnerEntities(false);
        }
      }
    };

    fetchPartnerEntities();

    return () => {
      cancelled = true;
    };
  }, [
    externalEntitiesRefreshKey,
    hasRequiredMappings,
    integrationCode,
    onExternalEntitiesError,
    partnerConfigId,
    t,
  ]);

  useEffect(() => {
    if (!partnerId) {
      setSavedMappings(null);
      return;
    }

    // Skip fetch if mappings were prefetched or already loaded
    if (savedMappings !== null) {
      return;
    }

    let cancelled = false;

    const fetchIntegrationMappings = async () => {
      try {
        const data = await getIntegrationMappings(partnerId, storeId);

        if (cancelled) {
          return;
        }

        setSavedMappings(Array.isArray(data) ? data : []);
      } catch {
        if (cancelled) {
          return;
        }

        setSavedMappings([]);
      }
    };

    fetchIntegrationMappings();

    return () => {
      cancelled = true;
    };
  }, [partnerId, savedMappings, storeId]);

  useEffect(() => {
    if (savedMappings === null) {
      return;
    }

    setRows((previous) => {
      if (previous.length > 0) {
        return previous;
      }

      const seededRows = savedMappings
        .filter((entry) => entry.storeId)
        .map(({ storeId: seededStoreId, externalId }) => ({
          ...createMappingRow(),
          localEntityId: seededStoreId,
          partnerEntityId: externalId,
        }));

      if (seededRows.length > 0) {
        return seededRows;
      }

      return [createMappingRow()];
    });
  }, [savedMappings]);

  useEffect(() => {
    if (savedMappings === null || hasReportedInitialRef.current || !onInitialMappingsLoaded) {
      return;
    }

    hasReportedInitialRef.current = true;

    const initialSnapshot = savedMappings
      .filter(({ storeId }) => storeId)
      .map(({ storeId, externalId }) => ({ storeId, externalId }));

    onInitialMappingsLoaded(initialSnapshot);
  }, [savedMappings, onInitialMappingsLoaded]);

  // Auto-select the only available store when the list is narrowed down to a single entry.
  useEffect(() => {
    if (!hasRequiredMappings) {
      return;
    }

    if (displayedStoresList.length !== 1) {
      return;
    }

    const [onlyStore] = displayedStoresList;

    setRows((previous) => {
      if (previous.length === 0) {
        return previous;
      }

      let changed = false;
      const nextRows = previous.map((row) => {
        if (row.localEntityId) {
          return row;
        }

        changed = true;
        return { ...row, localEntityId: onlyStore.id };
      });

      if (!changed) {
        return previous;
      }

      return nextRows;
    });
  }, [displayedStoresList, hasRequiredMappings]);

  // Measure table column width so the dropdown list width matches the column.
  useEffect(() => {
    if (!tableRef.current) {
      return;
    }

    const tableWidth = tableRef.current.offsetWidth;

    // Give each column dropdown slightly more room than the strict half-width
    setColumnWidth(Math.floor(tableWidth / 2));
  }, [hasRequiredMappings, isAnyEntityDataLoading]);

  const dropdownListStyle = useMemo(
    () => ({
      ...DROPDOWN_LIST_LAYER_STYLE,
      backgroundColor: '#FFFFFF',
      ...(columnWidth !== null ? { width: `${columnWidth}px` } : {}),
    }),
    [columnWidth],
  );

  const partnerEntityItems = useMemo(
    () => partnerEntities.map((entity) => buildCustomerItem(entity.id, entity.name)),
    [partnerEntities],
  );

  const isValid = useMemo(() => {
    if (savedMappings === null) {
      return false;
    }

    if (isAnyEntityDataLoading) {
      return false;
    }

    if (!hasRequiredMappings) {
      return true;
    }

    return areMappingRowsValid(rows, displayedStoresList, partnerEntities);
  }, [
    savedMappings,
    isAnyEntityDataLoading,
    hasRequiredMappings,
    rows,
    displayedStoresList,
    partnerEntities,
  ]);

  useEffect(() => {
    if (!onMappingChange) {
      return;
    }

    const mappings = hasRequiredMappings ? buildIntegrationMappingsPayload(rows) : [];

    onMappingChange({ isValid, mappings });
  }, [hasRequiredMappings, isValid, onMappingChange, rows]);

  const handleAddRow = useCallback(() => {
    setRows((prev) => [...prev, createMappingRow()]);
  }, []);

  const handleRemoveRow = useCallback((rowId) => {
    setRows((prev) => prev.filter((row) => row.rowId !== rowId));
  }, []);

  const updateRow = useCallback((rowId, patch) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.rowId !== rowId) {
          return row;
        }

        return { ...row, ...patch };
      }),
    );
  }, []);

  const renderExternalEntityMappingTable = () => {
    const displayedRows = rows.length > 0 ? rows : [createMappingRow()];

    const localColumnLabel = t('ADMIN.INTEGRATIONS.EXTERNAL_ENTITY_MAPPING_COL_INPULSE_STORE');

    const partnerColumnLabel =
      integrationCode === PENNYLANE_OAUTH2_CODE
        ? t('ADMIN.INTEGRATIONS.PENNYLANE_OAUTH2_OAUTH_ACTIVE_COL_PARTNER_CLIENT')
        : t('ADMIN.INTEGRATIONS.EXTERNAL_ENTITY_MAPPING_PARTNER_CLIENT', {
            partner: partnerName,
          });

    const skeletonCellSx = {
      borderLeft: `1px solid ${theme.colors.greys.light}`,
      borderBottom: `1px solid ${theme.colors.greys.light}`,
      padding: '0 16px',
    };

    if (savedMappings === null || isAnyEntityDataLoading) {
      return (
        <TableContainer
          component={Paper}
          ref={tableRef}
          sx={{
            boxShadow: 'none',
            maxWidth: '100%',
            overflow: 'hidden',
            borderRadius: '0px',
          }}
        >
          <Table
            aria-label="external entity mapping table"
            sx={{
              borderCollapse: 'separate',
              borderSpacing: 0,
              width: '100%',
              '& thead tr:first-of-type th:first-of-type, & thead tr:first-of-type td:first-of-type':
                { borderTopLeftRadius: '8px' },
              '& thead tr:first-of-type th:last-of-type, & thead tr:first-of-type td:last-of-type':
                { borderTopRightRadius: '8px' },
            }}
            aria-busy
          >
            <TableHead sx={{ height: 40 }}>
              <TableRow sx={{ backgroundColor: theme.colors.greys.lightest }}>
                <TableCell
                  sx={{
                    borderTop: `1px solid ${theme.colors.greys.light}`,
                    borderLeft: `1px solid ${theme.colors.greys.light}`,
                    borderBottom: `1px solid ${theme.colors.greys.light}`,
                    padding: '0 16px',
                  }}
                >
                  <Text color={ENUM_COLORS.DGREY} font={ENUM_FONTS.TEXT_SMALL}>
                    {localColumnLabel}
                  </Text>
                </TableCell>
                <TableCell
                  sx={{
                    borderTop: `1px solid ${theme.colors.greys.light}`,
                    borderLeft: `1px solid ${theme.colors.greys.light}`,
                    borderBottom: `1px solid ${theme.colors.greys.light}`,
                    padding: '0 16px',
                  }}
                >
                  <Text color={ENUM_COLORS.DGREY} font={ENUM_FONTS.TEXT_SMALL}>
                    {partnerColumnLabel}
                  </Text>
                </TableCell>
                <TableCell
                  aria-hidden="true"
                  sx={{
                    border: `1px solid ${theme.colors.greys.light}`,
                    padding: '0 8px',
                    width: 48,
                  }}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {[0, 1].map((skeletonIndex) => (
                <TableRow key={skeletonIndex} sx={{ height: 64 }}>
                  <TableCell sx={skeletonCellSx}>
                    <Skeleton
                      animation="wave"
                      height={32}
                      sx={{ borderRadius: '4px' }}
                      variant="rectangular"
                    />
                  </TableCell>
                  <TableCell sx={skeletonCellSx}>
                    <Skeleton
                      animation="wave"
                      height={32}
                      sx={{ borderRadius: '4px' }}
                      variant="rectangular"
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: '8px',
                      width: 48,
                      borderRight: `1px solid ${theme.colors.greys.light}`,
                      borderLeft: `1px solid ${theme.colors.greys.light}`,
                      borderBottom: `1px solid ${theme.colors.greys.light}`,
                    }}
                  >
                    <Skeleton
                      animation="wave"
                      height={32}
                      sx={{ borderRadius: '4px' }}
                      variant="rectangular"
                      width={32}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    return (
      <TableContainer
        component={Paper}
        ref={tableRef}
        sx={{
          boxShadow: 'none',
          maxWidth: '100%',
          // `overflow: visible` so absolutely positioned dropdown lists are not clipped when open.
          overflow: 'visible',
        }}
      >
        <Table
          aria-busy={isAnyEntityDataLoading}
          aria-label="external entity mapping table"
          sx={{
            borderCollapse: 'separate',
            borderSpacing: 0,
            overflow: 'visible',
            width: '100%',
            '& thead tr:first-of-type th:first-of-type, & thead tr:first-of-type td:first-of-type':
              { borderTopLeftRadius: '8px' },
            '& thead tr:first-of-type th:last-of-type, & thead tr:first-of-type td:last-of-type': {
              borderTopRightRadius: '8px',
            },
          }}
        >
          <TableHead sx={{ height: 40 }}>
            <TableRow
              sx={{
                backgroundColor: theme.colors.greys.lightest,
              }}
            >
              <TableCell
                sx={{
                  borderTop: `1px solid ${theme.colors.greys.light}`,
                  borderLeft: `1px solid ${theme.colors.greys.light}`,
                  borderBottom: `1px solid ${theme.colors.greys.light}`,
                  padding: '0 16px',
                }}
              >
                <Text color={ENUM_COLORS.DGREY} font={ENUM_FONTS.TEXT_SMALL}>
                  {localColumnLabel}
                </Text>
              </TableCell>
              <TableCell
                sx={{
                  borderTop: `1px solid ${theme.colors.greys.light}`,
                  borderLeft: `1px solid ${theme.colors.greys.light}`,
                  borderBottom: `1px solid ${theme.colors.greys.light}`,
                  padding: '0 16px',
                }}
              >
                <Text color={ENUM_COLORS.DGREY} font={ENUM_FONTS.TEXT_SMALL}>
                  {partnerColumnLabel}
                </Text>
              </TableCell>
              <TableCell
                aria-hidden="true"
                sx={{
                  border: `1px solid ${theme.colors.greys.light}`,
                  padding: '0 8px',
                  width: 48,
                }}
              />
            </TableRow>
          </TableHead>
          <TableBody sx={{ overflow: 'visible' }}>
            {displayedRows.map((row) => {
              const usedLocalIds = displayedRows
                .filter((other) => other.rowId !== row.rowId && other.localEntityId)
                .map((other) => other.localEntityId);

              const usedPartnerIds = displayedRows
                .filter((other) => other.rowId !== row.rowId && other.partnerEntityId)
                .map((other) => other.partnerEntityId);

              const localDropdownItems = displayedStoresList
                .filter((store) => store?.id)
                .map((store) => {
                  const label = getStoreLabel(store);
                  const isAlreadyLinked = usedLocalIds.includes(store.id);

                  if (isAlreadyLinked) {
                    return buildAlreadyLinkedItem(store.id, label);
                  }

                  return { id: store.id, value: label };
                });

              const rowPartnerEntityItems = partnerEntityItems.map((item) => {
                const isAlreadyLinked = usedPartnerIds.includes(String(item.id));

                if (isAlreadyLinked) {
                  return buildCustomerItem(String(item.id), item.value, { isLinked: true });
                }

                return item;
              });

              const selectedLocalEntity = (() => {
                if (!row.localEntityId) {
                  return null;
                }

                const found = displayedStoresList.find(({ id }) => id === row.localEntityId);

                if (found) {
                  return {
                    id: found.id,
                    value: getStoreLabel(found),
                  };
                }

                if (!displayedStoresList.length) {
                  return null;
                }

                const unknownStoreI18nKey = linkedStoreIds
                  ? 'ADMIN.INTEGRATIONS.EXTERNAL_ENTITY_MAPPING_UNKNOWN_STORE_KITCHEN'
                  : 'ADMIN.INTEGRATIONS.EXTERNAL_ENTITY_MAPPING_UNKNOWN_STORE';

                return {
                  id: row.localEntityId,
                  value: t(unknownStoreI18nKey),
                };
              })();

              const foundCustomer =
                row.partnerEntityId &&
                partnerEntities.find((entity) => String(entity.id) === row.partnerEntityId);

              const selectedPartnerEntity = (() => {
                if (!row.partnerEntityId) {
                  return null;
                }

                if (foundCustomer) {
                  return {
                    id: row.partnerEntityId,
                    value: foundCustomer.name,
                  };
                }

                if (!partnerEntities.length) {
                  return {
                    id: row.partnerEntityId,
                    value: row.partnerEntityId,
                  };
                }

                return {
                  id: row.partnerEntityId,
                  value:
                    integrationCode === PENNYLANE_OAUTH2_CODE
                      ? t('ADMIN.INTEGRATIONS.PENNYLANE_OAUTH2_OAUTH_ACTIVE_UNKNOWN_CUSTOMER')
                      : t('ADMIN.INTEGRATIONS.TOKEN_INTEGRATION_OAUTH_ACTIVE_UNKNOWN_EXTERNAL'),
                };
              })();

              const isStoreUnknown =
                row.localEntityId !== null &&
                displayedStoresList.length > 0 &&
                !displayedStoresList.find((store) => store.id === row.localEntityId);

              const isCustomerUnknown =
                row.partnerEntityId !== null &&
                partnerEntities.length > 0 &&
                !partnerEntities.find((entity) => String(entity.id) === row.partnerEntityId);

              const partnerCellHasError = isCustomerUnknown;

              return (
                <TableRow
                  key={row.rowId}
                  sx={{
                    height: 64,
                    overflow: 'visible',
                  }}
                >
                  <TableCell
                    sx={{
                      ...getMappingCellSx(theme),
                      ...(isStoreUnknown && {
                        backgroundColor: theme.colors.infoLightRed1,
                        border: `2px solid ${theme.colors.infoRed1}`,
                      }),
                    }}
                  >
                    <CellContent>
                      <DropdownContainer $isError={isStoreUnknown}>
                        <Dropdown
                          customListStyle={dropdownListStyle}
                          height="100%"
                          isErrorState={isStoreUnknown}
                          isSearchDisabled={localDropdownItems.length <= 8}
                          items={localDropdownItems}
                          placeholder={t('GENERAL.DROPDOWN_PLACEHOLDER')}
                          searchPlaceholder={t('GENERAL.SEARCH')}
                          selectedItem={selectedLocalEntity}
                          width="100%"
                          isUniqueSelection
                          onSelectionChange={(selectedItem, _, isUnselected) => {
                            if (isUnselected || !selectedItem) {
                              updateRow(row.rowId, { localEntityId: null });
                              return;
                            }

                            updateRow(row.rowId, { localEntityId: selectedItem.id });
                          }}
                        />
                      </DropdownContainer>
                    </CellContent>
                  </TableCell>
                  <TableCell
                    sx={{
                      ...getMappingCellSx(theme),
                      ...(partnerCellHasError && {
                        backgroundColor: theme.colors.infoLightRed1,
                        border: `2px solid ${theme.colors.infoRed1}`,
                      }),
                    }}
                  >
                    <CellContent>
                      <DropdownContainer $isError={isCustomerUnknown}>
                        <Dropdown
                          customListStyle={dropdownListStyle}
                          height="100%"
                          isErrorState={isCustomerUnknown}
                          isSearchDisabled={false}
                          items={rowPartnerEntityItems}
                          placeholder={t('GENERAL.DROPDOWN_PLACEHOLDER')}
                          searchPlaceholder={t('GENERAL.SEARCH')}
                          selectedItem={selectedPartnerEntity}
                          width="100%"
                          isUniqueSelection
                          onSelectionChange={(selectedItem, _, isUnselected) => {
                            if (isUnselected || !selectedItem) {
                              updateRow(row.rowId, { partnerEntityId: null });
                              return;
                            }

                            updateRow(row.rowId, {
                              partnerEntityId: String(selectedItem.id),
                            });
                          }}
                        />
                      </DropdownContainer>
                    </CellContent>
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: '8px',
                      verticalAlign: 'top',
                      width: 48,
                      borderRight: `1px solid ${theme.colors.greys.light}`,
                      borderBottom: `1px solid ${theme.colors.greys.light}`,
                      borderLeft: `1px solid ${theme.colors.greys.light}`,
                    }}
                  >
                    <RowActions>
                      <RemoveRowButton
                        aria-label={t('GENERAL.DELETE')}
                        disabled={isAnyEntityDataLoading}
                        type="button"
                        onClick={() => {
                          handleRemoveRow(row.rowId);
                        }}
                      >
                        <img alt="" src="/images/inpulse/trash-black-small.svg" />
                      </RemoveRowButton>
                    </RowActions>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  let mappingIntroText = t('ADMIN.INTEGRATIONS.TOKEN_MAPPING_INTRO');
  if (integrationCode === PENNYLANE_OAUTH2_CODE) {
    if (isKitchenContext) {
      mappingIntroText = t('ADMIN.INTEGRATIONS.PENNYLANE_OAUTH2_OAUTH_ACTIVE_INTRO_KITCHEN');
    } else {
      mappingIntroText = t('ADMIN.INTEGRATIONS.PENNYLANE_OAUTH2_OAUTH_ACTIVE_INTRO');
    }
  }

  return (
    <ExternalEntityMappingPanelRoot>
      <IntroText>{mappingIntroText}</IntroText>

      {hasRequiredMappings ? (
        <MappingTablesHost>
          {renderExternalEntityMappingTable()}

          {savedMappings !== null && (
            <AddRowButton disabled={isAnyEntityDataLoading} type="button" onClick={handleAddRow}>
              {t('ADMIN.INTEGRATIONS.EXTERNAL_ENTITY_MAPPING_ADD_ROW')}
            </AddRowButton>
          )}
        </MappingTablesHost>
      ) : null}
    </ExternalEntityMappingPanelRoot>
  );
};
